import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiClient, TOKEN_KEY } from '@/api/api';
import { extractErrorMessage } from '@/composables/apiError';
import type { PermissionMode, PermissionRequirement } from '@/permissions';
import { permissionKeys } from '@/permissions';

type PermissionGrant = {
  key?: unknown;
};

type MePermissionsResponse = {
  permissions?: unknown;
  grants?: unknown;
  roles?: unknown;
  employeeId?: unknown;
};

/** number = linked employee, null = the server says there is none, undefined = unknown. */
function normalizeEmployeeId(raw: unknown): number | null | undefined {
  if (typeof raw === 'number' && raw > 0) return raw;
  if (raw === null) return null;
  return undefined;
}

export const PERMISSIONS_LOAD_TIMEOUT_MS = 12_000;

function normalizePermissionList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (typeof item === 'string') return [item];
    if (item && typeof item === 'object') {
      const key = (item as PermissionGrant).key;
      return typeof key === 'string' ? [key] : [];
    }
    return [];
  });
}

function normalizeEffectivePermissions(response: MePermissionsResponse): string[] {
  return [
    ...normalizePermissionList(response.permissions),
    ...normalizePermissionList(response.grants),
  ];
}

function readCredentialItem(key: string): string {
  try {
    return globalThis.localStorage?.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function credentialSignature(): string {
  return readCredentialItem(TOKEN_KEY);
}

function permissionsTimeoutError(): Error & { code: string } {
  const error = new Error(`Permission load timed out after ${PERMISSIONS_LOAD_TIMEOUT_MS}ms`);
  return Object.assign(error, { code: 'ERP_PERMISSIONS_TIMEOUT' });
}

async function withPermissionTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      reject(permissionsTimeoutError());
    }, PERMISSIONS_LOAD_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}

export const usePermissionStore = defineStore('erp-permissions', () => {
  const permissions = ref<string[]>([]);
  /**
   * The caller's employee in the active company. `null` only when the server
   * answered that the account has none — the shell then keeps the account out of
   * ERP. `undefined` (not loaded, or an older backend) never blocks.
   */
  const employeeId = ref<number | null | undefined>(undefined);
  const loaded = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);
  let inFlight: Promise<void> | null = null;
  let inFlightCredential: string | null = null;
  let loadedCredential: string | null = null;
  /**
   * Per-load sequence number, deciding whether an arriving response is still
   * the current one. Comparing the credential signature instead was the bug:
   *
   * On a 401 the api client has the shell refresh the bearer and replays the
   * request (`make401Handler` in `api/api.ts`). localStorage then holds a
   * different token — for the very same account. The signature check read that
   * as an account switch and discarded its own successful response:
   * `permissions` stayed empty, `loaded` stayed false, and nothing threw. The
   * shell's route guard then checks fail-closed against the empty set, so every
   * cold start with an expired token landed on `/erp/permission-denied`.
   *
   * An account switch stays covered: the shell router calls `clear()` when the
   * credentials change, and `clear()` advances the number, so a response still
   * in flight for the previous account no longer applies.
   */
  let generation = 0;

  const permissionSet = computed(() => new Set(permissions.value));

  function hasWildcard(): boolean {
    return permissionSet.value.has('*');
  }

  function canAll(requirement: PermissionRequirement): boolean {
    const keys = permissionKeys(requirement);
    if (keys.length === 0) return true;
    if (hasWildcard()) return true;
    return keys.every((key) => permissionSet.value.has(key));
  }

  function canAny(requirement: PermissionRequirement): boolean {
    const keys = permissionKeys(requirement);
    if (keys.length === 0) return true;
    if (hasWildcard()) return true;
    return keys.some((key) => permissionSet.value.has(key));
  }

  function can(requirement: PermissionRequirement, mode: PermissionMode = 'all'): boolean {
    return mode === 'any' ? canAny(requirement) : canAll(requirement);
  }

  async function ensureLoaded(force = false): Promise<void> {
    const credential = credentialSignature();
    if (!force && loaded.value && loadedCredential === credential) return;
    if (inFlight && inFlightCredential === credential) return inFlight;

    const requestGeneration = ++generation;
    loading.value = true;
    error.value = null;
    const request = withPermissionTimeout(
      apiClient.get<MePermissionsResponse>('/me/permissions', {
        timeout: PERMISSIONS_LOAD_TIMEOUT_MS,
      })
    )
      .then((response) => {
        if (generation !== requestGeneration) return;
        const keys = normalizeEffectivePermissions(response.data);
        permissions.value = [...new Set(keys)].sort();
        employeeId.value = normalizeEmployeeId(response.data.employeeId);
        loaded.value = true;
        // Deliberately the CURRENT signature, not the one captured on send: if
        // the api client refreshed the bearer along the way, the result belongs
        // to the refreshed one — otherwise the next call reloads immediately.
        loadedCredential = credentialSignature();
      })
      .catch((e) => {
        if (generation === requestGeneration) {
          permissions.value = [];
          employeeId.value = undefined;
          loaded.value = false;
          loadedCredential = null;
          error.value = extractErrorMessage(e);
        }
        throw e;
      })
      .finally(() => {
        if (inFlight === request) {
          loading.value = false;
          inFlight = null;
          inFlightCredential = null;
        }
      });

    inFlight = request;
    inFlightCredential = credential;
    return request;
  }

  function clear(): void {
    // Advance the number so a response still in flight cannot install the
    // previous account's permissions after the fact.
    generation += 1;
    permissions.value = [];
    employeeId.value = undefined;
    loaded.value = false;
    loading.value = false;
    error.value = null;
    inFlight = null;
    inFlightCredential = null;
    loadedCredential = null;
  }

  return {
    permissions,
    employeeId,
    permissionSet,
    loaded,
    loading,
    error,
    ensureLoaded,
    refresh: () => ensureLoaded(true),
    clear,
    can,
    canAny,
    canAll,
  };
});
